import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import type { UnverifiedInfo } from "@/features/customers/types";
import { InfoIcon } from "@phosphor-icons/react";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";

interface UnverifiedInfoIndicatorProps {
  unverifiedInfos?: UnverifiedInfo[];
}

export function UnverifiedInfoIndicator({
  unverifiedInfos,
}: UnverifiedInfoIndicatorProps) {
  const { t } = useTranslation("customers");
  const { fromNow } = useDateFormat();
  if (!unverifiedInfos || unverifiedInfos.length === 0) {
    return null;
  }

  const getSourceLabel = (source: string): string => {
    const key = `unverified.sources.${source}` as const;
    const translated = t(key);
    return translated === key ? source : translated;
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <InfoIcon className="inline-block h-4 w-4 ml-2 text-muted-foreground hover:text-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-sm">
          <div className="space-y-2 py-1">
            {unverifiedInfos.map((info, idx) => (
              <div key={idx} className="text-xs space-y-1 pb-2">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant={"secondary"} className="text-xs h-4 px-1">
                    {getSourceLabel(info.source)}
                  </Badge>
                  <span className="text-muted-foreground">
                    {fromNow(info.date)}
                  </span>
                </div>
                <div className="font-mono">"{info.value}"</div>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
